import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { Card, IconButton, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { useTranslation } from "@/shared/hooks";

// Порт референсного ContentBody/ContentDescription: карточка с описанием,
// свёрнутая до трёх строк, стрелка справа сверху разворачивает. Описание —
// плоский текст (маркдауна на site нет), поэтому Typography c pre-wrap
// вместо dangerouslySetInnerHTML.
export const ContentDescription: FC<{ children: string }> = ({ children }) => {
  const { t } = useTranslation();
  const textRef = useRef<HTMLDivElement | null>(null);
  const [expandable, setExpandable] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;
    setExpandable(element.scrollHeight > element.clientHeight);
  }, [children]);

  const onClickButton = useCallback(() => setExpanded((value) => !value), []);

  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        p: 2,
        pr: 6,
        border: "1px solid",
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.30)"
            : "rgba(0, 0, 0, 0.12)",
      }}
    >
      <Typography
        component="div"
        variant="body2"
        color="text.secondary"
        ref={textRef}
        sx={{
          overflow: "hidden",
          whiteSpace: "pre-wrap",
          maxHeight: {
            xs: expanded ? "none" : "calc(0.8rem * 3 * 1.43)",
            sm: expanded ? "none" : "calc(1.1rem * 3 * 1.43)",
          },
          fontSize: { xs: "0.8rem", sm: "1.1rem" },
        }}
      >
        {children}
      </Typography>

      {expandable && (
        <IconButton
          onClick={onClickButton}
          sx={{ position: "absolute", top: 6, right: 6 }}
          aria-label={
            expanded
              ? t("video.description.collapse")
              : t("video.description.expand")
          }
        >
          {!expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowLeftIcon />}
        </IconButton>
      )}
    </Card>
  );
};
